import Vue from "vue";
import Router from "vue-router";
import {adminRoutes} from "@/routes/admin.router";
import {authRouter} from "@/routes/auth.router";
import {agentRouter} from "@/routes/agent.router";
import {qualityRouter} from "@/routes/quality.router";
import {salesManagerRouter} from "@/routes/salesManager.router";
import {fundingManagerRouter} from "@/routes/fundingManager.router";
import {commonRouter} from "@/routes/common.router";
import {accountingRouter} from "@/routes/accounting.router";
import {customerServiceSupervisorRoutes} from "@/routes/customerServiceSupervisor.router";
import {store} from "@/store/index";

Vue.use(Router);

const router = new Router({
  mode: "history",
  routes: [
      ...authRouter,
      ...adminRoutes,
      ...agentRouter,
      ...salesManagerRouter,
      ...fundingManagerRouter,
      ...qualityRouter,
      ...accountingRouter,
      ...customerServiceSupervisorRoutes,
      ...commonRouter,
  ],
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
});

// Track if we're already processing a questionnaire check to prevent recursion
let isCheckingQuestionnaire = false;

function showQuestionnairePopup(questionId, message = "لديك استبيان جديد لم يتم الإجابة عليه، هل ترغب في فتحه الآن؟") {
  const app = router.app;
  if (app && app.$root) {
    app.$root.$emit("popup-btn", "error", message, "فتح الاستبيان", questionId);
  } else if (Vue.prototype.$root) {
    Vue.prototype.$root.$emit("popup-btn", "error", message, "فتح الاستبيان", questionId);
  }
}

function checkAndShowQuestionnaire(to, from) {
  const userRole = localStorage.getItem('role');
  
  // Prevent recursion and only check for role funding manager (role '2')
  if (userRole !== '2' || to.name === 'questionnaire_details' || isCheckingQuestionnaire) {
    return;
  }

  if (store.state.NextUnansweredQuestion) {
    setTimeout(() => {
      showQuestionnairePopup(
        store.state.NextUnansweredQuestion.id,
        "لديك استبيان جديد لم يتم الإجابة عليه، هل ترغب في فتحه الآن؟"
      );
    }, 1000);
  }
}

// Helper function to get cookie value
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

// Helper function to check authentication
function isAuthenticated() {
  return !!getCookie('wsataJwt');
}

// Helper function to get role-based redirect path
function getRoleBasedRedirect(userRole) {
  // Customer Service users are stored as role '0' with a separate flag.
  if (userRole === '0' && localStorage.getItem('is_customer_service') === '1') {
    return '/customer-service/requests/all';
  }

  const roleRedirects = {
    '7': '/admin',
    '16': '/customer-service-supervisor',
    '1': '/salesManager', 
    '2': '/funding-manager',
    '5': '/quality/requests/all'
  };

  if (userRole == '8') {
    const accountType = localStorage.getItem('account_type');
    const accountTypeRedirects = {
      '0': '/accounting/tsaheelreports',
      '1': '/accounting/wasatareports', 
    };
    
    return accountTypeRedirects[accountType] || '/accounting/wasatareports';
  }

  return roleRedirects[userRole] || null;
}

router.beforeEach(async (to, from, next) => {
  try {
    // Handle authentication first
    const isAuth = isAuthenticated();
    
    if (!isAuth && to.path !== '/login') {
      next('/login');
      return;
    }
    
    if (isAuth && to.path === '/login') {
      next('/');
      return;
    }

    // Handle role-based redirects for 'all' route
    if (to.name === 'all') {
      const userRole = localStorage.getItem('role');
      const redirectPath = getRoleBasedRedirect(userRole);
      
      if (redirectPath) {
        next(redirectPath);
        return;
      }
    }
    next();
  } catch (error) {
    console.error('Router beforeEach error:', error);
    next(); // Continue navigation even if there's an error
  }
});

router.beforeResolve((to, from, next) => {
  // Set page title
  Vue.nextTick(() => {
    document.title = to.meta.page_name || "شركة الوساطة العقارية";
  });

  // Check questionnaire after navigation resolves
  Vue.nextTick(() => {
    checkAndShowQuestionnaire(to, from);
  });
  // Show full page transition preloader for route changes
  if (from.name && to.name !== from.name) {
    // Import store dynamically to avoid circular dependency
    import('@/store').then(({ store }) => {
      store.dispatch('preloader/showFullPageLoading', {
        loadingText: `جاري تحميل ${to.meta.page_name || to.name}...`,
        subText: 'يرجى الانتظار',
        spinnerType: 'wave',
        autoHide: true,
        autoHideDelay: 1500
      });
    });
  }

  if (to.name === 'all') {
    const userRole = localStorage['role'];
  }

  next();
});

router.afterEach((to, from) => {
  const userRole = localStorage.getItem('role');
  
  // Only show questionnaire popup for role 2, not on questionnaire page, and prevent recursion
  if (userRole === '2' && to.name !== 'questionnaire_details' && !isCheckingQuestionnaire) {
    setTimeout(() => {
      if (store.state.NextUnansweredQuestion) {
        showQuestionnairePopup(store.state.NextUnansweredQuestion.id);
      }
    }, 1000);
  }
});

// Add error handler for navigation failures
router.onError((error) => {
  console.error('Router navigation error:', error);
});

// Hide preloader after route change
router.afterEach((to, from) => {
  // Import store dynamically to avoid circular dependency
  import('@/store').then(({ store }) => {
    setTimeout(() => {
      store.dispatch('preloader/hidePreloader');
    }, 500);
  });
});

export default router;
